import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { kpiRowSelectors } from '../../../../../../../app/resource/kpi-row/kpi-row.selectors';
import { IKpiRow } from '../../../../../../../app/resource/kpi-row/kpi-row.types';
import { useSelectWithParams } from '../../../../../../../utils/selector.utils';
import { useScrollCancelDrag } from '../../../../../../../utils/useScrollCancelDrag';
import { ScoreRow } from '../ScoreRow';
import { ISortableGroupedByUserScoreListProps } from './types';

function groupRowsByUser(kpiRows: IKpiRow[]): Record<string, string[]> {
  return kpiRows.reduce((acc, row) => {
    const userId = row.userId;
    if (!acc[userId]) {
      acc[userId] = [];
    }
    acc[userId].push(row.id);
    return acc;
  }, {} as Record<string, string[]>);
}

export const SortableGroupedByUserScoreList: FunctionComponent<ISortableGroupedByUserScoreListProps> = ({
  kpiRowIds,
  filterId,
  onScoreSelection,
  selectedScoreIds,
  onReorder,
}) => {
  const kpiRows = useSelectWithParams(kpiRowSelectors.selectResourcesById, kpiRowIds) as IKpiRow[];

  // const kpiRowIdsKey = kpiRowIds.join(',');
  const groupedRows = useMemo(() => groupRowsByUser(kpiRows), [kpiRows]);

  const { sensors, handleDragStart, handleDragEnd } = useScrollCancelDrag();

  const handleDrop = useCallback(
    (result: DropResult) => {
      handleDragEnd();
      if (result.reason === 'CANCEL' || !result.destination || !onReorder) {
        return;
      }
      if (result.source.index === result.destination.index) {
        return;
      }

      const userId = result.source.droppableId;
      const rowIds = groupedRows[userId] || [];
      const sourceId = result.draggableId;
      const destId = rowIds[result.destination.index];

      const globalOrder = [...kpiRowIds];
      const sourceGlobalIndex = globalOrder.indexOf(sourceId);
      const destGlobalIndex = globalOrder.indexOf(destId);

      if (sourceGlobalIndex === -1 || destGlobalIndex === -1) {
        return;
      }

      globalOrder.splice(sourceGlobalIndex, 1);
      globalOrder.splice(destGlobalIndex, 0, sourceId);

      onReorder(globalOrder);
    },
    [groupedRows, handleDragEnd, kpiRowIds, onReorder],
  );

  return (
    <DragDropContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDrop}>
      {Object.entries(groupedRows).map(([userId, rowIds]) => (
        <Droppable key={userId} droppableId={userId} direction="vertical" type="column">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps} style={{ marginBottom: '20px' }}>
              {rowIds.map((scoreId, index) => (
                <Draggable key={`draggable-row-${scoreId}`} draggableId={scoreId} index={index}>
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                      <ScoreRow
                        scoreId={scoreId}
                        filterId={filterId}
                        onCheckboxChange={onScoreSelection}
                        isSelected={selectedScoreIds.includes(scoreId)}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      ))}
    </DragDropContext>
  );
};
